import React, { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { format, addDays, startOfWeek } from 'date-fns';
import { todayIST, fmtDayDate } from '../utils/time';
import DailyReportScheduleModal from '../components/DailyReportScheduleModal';

const STATUS_COLORS = {
  scheduled: 'badge-info',
  active: 'badge-warning',
  completed: 'badge-success',
  missed: 'badge-danger',
};

const SHIFT_TIMES = [
  { label: 'Morning   06:00–14:00', start: '06:00', end: '14:00' },
  { label: 'Afternoon 14:00–22:00', start: '14:00', end: '22:00' },
  { label: 'Night     22:00–06:00', start: '22:00', end: '06:00' },
  { label: 'Custom', start: '', end: '' },
];

const empty = {
  name: 'Patrol Shift',
  route_id: '', guard_id: '',
  shift_date: todayIST(),
  start_time: '22:00', end_time: '06:00',
  notes: '', preset: 2,
};

export default function Shifts() {
  const [shifts, setShifts] = useState([]);
  const [guards, setGuards] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('week'); // 'week' | 'list'
  const [filterDate, setFilterDate] = useState(todayIST());
  const [filterGuard, setFilterGuard] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [showScheduleModal, setShowScheduleModal] = useState(false);

  const load = async () => {
    setLoading(true);
    const params = {};
    if (filterGuard) params.guard_id = filterGuard;
    const [s, g, r] = await Promise.all([
      axios.get('/api/shifts', { params }),
      axios.get('/api/users/guards'),
      axios.get('/api/routes'),
    ]);
    setShifts(s.data);
    setGuards(g.data);
    setRoutes(r.data.filter(r => r.is_active));
    setLoading(false);
  };

  useEffect(() => { load(); }, [filterGuard]);

  const openCreate = (date = null, guard_id = '') => {
    setForm({ ...empty, shift_date: date || todayIST(), guard_id });
    setEditing(null);
    setShowForm(true);
  };

  const openEdit = (shift) => {
    setForm({
      name: shift.name, route_id: shift.route_id, guard_id: shift.guard_id,
      shift_date: shift.shift_date, start_time: shift.start_time, end_time: shift.end_time,
      notes: shift.notes || '', preset: 3,
    });
    setEditing(shift.id);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await axios.put(`/api/shifts/${editing}`, form);
        toast.success('Shift updated');
      } else {
        await axios.post('/api/shifts', form);
        toast.success('Shift assigned');
      }
      setShowForm(false);
      setEditing(null);
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  };

  const deleteShift = async (id, name) => {
    if (!window.confirm(`Delete shift "${name}"?`)) return;
    await axios.delete(`/api/shifts/${id}`);
    toast.success('Shift deleted');
    load();
  };

  const applyPreset = (idx) => {
    const p = SHIFT_TIMES[idx];
    setForm(f => ({ ...f, preset: idx, start_time: p.start, end_time: p.end }));
  };

  // Week view days
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  const shiftsOnDay = (date) => {
    const d = format(date, 'yyyy-MM-dd');
    return shifts.filter(s => s.shift_date === d);
  };

  return (
    <div>
      <div className="page-header">
        <div className="page-title">📅 Shift Roster</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <button type="button" className="btn btn-sm" style={{ background: '#f8fafc', border: '1.5px solid #0284c7', color: '#0284c7', fontWeight: 600 }} onClick={() => setShowScheduleModal(true)}>⏰ Daily Email Report Schedule</button>
          <button className={`btn btn-sm ${view === 'week' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setView('week')}>📅 Week</button>
          <button className={`btn btn-sm ${view === 'list' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setView('list')}>📋 List</button>
          <button className="btn btn-primary btn-sm" onClick={() => openCreate()}>+ Assign Shift</button>
        </div>
      </div>

      {/* Filters */}
      <div className="card mb-4" style={{ padding: '12px 16px' }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <label style={{ fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap' }}>Filter Guard:</label>
            <select className="form-input" style={{ width: 200 }} value={filterGuard} onChange={e => setFilterGuard(e.target.value)}>
              <option value="">All Guards</option>
              {guards.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
          </div>
          {view === 'week' && (
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginLeft: 'auto' }}>
              <button className="btn btn-sm btn-secondary" onClick={() => setWeekStart(d => addDays(d, -7))}>← Prev</button>
              <span style={{ fontSize: 13, fontWeight: 500 }}>
                {format(weekStart, 'MMM d')} – {format(addDays(weekStart, 6), 'MMM d, yyyy')}
              </span>
              <button className="btn btn-sm btn-secondary" onClick={() => setWeekStart(d => addDays(d, 7))}>Next →</button>
              <button className="btn btn-sm btn-primary" onClick={() => setWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }))}>Today</button>
            </div>
          )}
        </div>
      </div>

      {/* ── WEEK CALENDAR VIEW ── */}
      {view === 'week' && (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', borderBottom: '2px solid #e5e7eb' }}>
            {weekDays.map(day => {
              const isToday = format(day, 'yyyy-MM-dd') === todayIST();
              return (
                <div key={day.toISOString()} style={{
                  padding: '10px 8px', textAlign: 'center', borderRight: '1px solid #f3f4f6',
                  background: isToday ? '#eff6ff' : '#f9fafb',
                }}>
                  <div style={{ fontSize: 11, color: '#6b7280', textTransform: 'uppercase', fontWeight: 600 }}>{format(day, 'EEE')}</div>
                  <div style={{
                    fontSize: 18, fontWeight: 700, marginTop: 2,
                    color: isToday ? '#4361ee' : '#111',
                    background: isToday ? '#dbeafe' : 'transparent',
                    borderRadius: '50%', width: 32, height: 32,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    margin: '2px auto 0',
                  }}>{format(day, 'd')}</div>
                </div>
              );
            })}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', minHeight: 300 }}>
            {weekDays.map(day => {
              const dayShifts = shiftsOnDay(day);
              const dateStr = format(day, 'yyyy-MM-dd');
              return (
                <div key={day.toISOString()} style={{ borderRight: '1px solid #f3f4f6', padding: '8px 6px', minHeight: 120 }}>
                  {dayShifts.map(s => (
                    <div key={s.id} onClick={() => openEdit(s)}
                      style={{
                        background: s.status === 'completed' ? '#d1fae5' : s.status === 'missed' ? '#fee2e2' : s.status === 'active' ? '#fef3c7' : '#dbeafe',
                        borderRadius: 8, padding: '6px 8px', marginBottom: 5,
                        cursor: 'pointer', fontSize: 11, lineHeight: 1.4,
                        borderLeft: `3px solid ${s.status === 'completed' ? '#22c55e' : s.status === 'missed' ? '#ef233c' : s.status === 'active' ? '#f59e0b' : '#4361ee'}`,
                      }}>
                      <div style={{ fontWeight: 700, fontSize: 12 }}>👮 {s.guard_name}</div>
                      <div style={{ color: '#374151' }}>{s.start_time}–{s.end_time}</div>
                      <div style={{ color: '#6b7280' }}>{s.route_name}</div>
                      <span className={`badge ${STATUS_COLORS[s.status]}`} style={{ marginTop: 3, fontSize: 10 }}>{s.status}</span>
                    </div>
                  ))}
                  <button onClick={() => openCreate(dateStr)}
                    style={{ width: '100%', padding: '4px', background: 'none', border: '1px dashed #d1d5db', borderRadius: 6, cursor: 'pointer', color: '#9ca3af', fontSize: 11 }}>
                    + Add
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── LIST VIEW ── */}
      {view === 'list' && (
        <div className="card">
          {loading ? <div style={{ padding: 20 }}>Loading...</div> : (
            <div className="table-scroll">
              <table className="table">
                <thead>
                  <tr><th>Date</th><th>Guard</th><th>Route</th><th>Time</th><th>Status</th><th>Notes</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {shifts.map(s => (
                    <tr key={s.id}>
                      <td><strong>{fmtDayDate(s.shift_date)}</strong></td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ background: '#dbeafe', color: '#1e40af', borderRadius: '50%', width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, flexShrink: 0 }}>👮</span>
                          <div>
                            <div style={{ fontWeight: 500, fontSize: 13 }}>{s.guard_name}</div>
                            {s.guard_phone && <div style={{ fontSize: 11, color: '#9ca3af' }}>{s.guard_phone}</div>}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div style={{ fontWeight: 500, fontSize: 13 }}>{s.route_name}</div>
                        <div style={{ fontSize: 11, color: '#9ca3af' }}>{s.site_name}</div>
                      </td>
                      <td style={{ fontSize: 13 }}>
                        <strong>{s.start_time}</strong> – {s.end_time}
                      </td>
                      <td><span className={`badge ${STATUS_COLORS[s.status]}`}>{s.status}</span></td>
                      <td style={{ fontSize: 12, color: '#6b7280', maxWidth: 120 }}>{s.notes || '—'}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button className="btn btn-sm btn-secondary" onClick={() => openEdit(s)}>Edit</button>
                          <button className="btn btn-sm btn-danger" onClick={() => deleteShift(s.id, s.name)}>Del</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {shifts.length === 0 && (
                    <tr><td colSpan={7} style={{ textAlign: 'center', color: '#9ca3af', padding: 24 }}>
                      No shifts yet. Click "+ Assign Shift" to create one.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── SHIFT FORM MODAL ── */}
      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: 'white', borderRadius: 16, padding: 28, width: '100%', maxWidth: 520, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div style={{ fontSize: 18, fontWeight: 700 }}>{editing ? '✏️ Edit Shift' : '➕ Assign Shift'}</div>
              <button onClick={() => { setShowForm(false); setEditing(null); }}
                style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#6b7280' }}>✕</button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label">Shift Name</label>
                <input className="form-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required />
              </div>

              <div className="grid-2">
                <div className="form-group">
                  <label className="form-label">Assign to Guard *</label>
                  <select className="form-input" value={form.guard_id} onChange={e => setForm({ ...form, guard_id: e.target.value })} required>
                    <option value="">Select guard...</option>
                    {guards.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Assign Route *</label>
                  <select className="form-input" value={form.route_id} onChange={e => setForm({ ...form, route_id: e.target.value })} required>
                    <option value="">Select route...</option>
                    {routes.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Shift Date *</label>
                <input type="date" className="form-input" value={form.shift_date} onChange={e => setForm({ ...form, shift_date: e.target.value })} required />
              </div>

              <div className="form-group">
                <label className="form-label">Shift Time Preset</label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 6, marginBottom: 10 }}>
                  {SHIFT_TIMES.map((p, i) => (
                    <button key={i} type="button"
                      onClick={() => applyPreset(i)}
                      style={{
                        padding: '8px 10px', borderRadius: 8, cursor: 'pointer', textAlign: 'left',
                        border: `2px solid ${form.preset === i ? '#4361ee' : '#e5e7eb'}`,
                        background: form.preset === i ? '#eff6ff' : 'white',
                        fontSize: 12, fontWeight: form.preset === i ? 600 : 400,
                      }}>
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid-2">
                <div className="form-group">
                  <label className="form-label">Start Time *</label>
                  <input type="time" className="form-input" value={form.start_time} onChange={e => setForm({ ...form, start_time: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label className="form-label">End Time *</label>
                  <input type="time" className="form-input" value={form.end_time} onChange={e => setForm({ ...form, end_time: e.target.value })} required />
                </div>
              </div>

              {editing && (
                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select className="form-input" value={form.status || 'scheduled'} onChange={e => setForm({ ...form, status: e.target.value })}>
                    <option value="scheduled">Scheduled</option>
                    <option value="active">Active</option>
                    <option value="completed">Completed</option>
                    <option value="missed">Missed</option>
                  </select>
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea className="form-input" rows={2} placeholder="Special instructions..."
                  value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
              </div>

              <div style={{ display: 'flex', gap: 8 }}>
                <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>
                  {editing ? 'Update Shift' : 'Assign Shift'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => { setShowForm(false); setEditing(null); }}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      <DailyReportScheduleModal isOpen={showScheduleModal} onClose={() => setShowScheduleModal(false)} />
    </div>
  );
}
